import { Box, Typography, useTheme } from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PROMO_MARQUEE_HEIGHT_PX } from './headerConstants';

const REPEAT_COUNT = 4;

const segmentTypographySx = {
    flexShrink: 0,
    px: { xs: 2.5, sm: 4 },
    fontWeight: 600,
    fontSize: { xs: 12, sm: 13 },
    letterSpacing: 0.35,
    whiteSpace: 'nowrap',
    lineHeight: 1,
} as const;

function MarqueeSegment({
    before,
    highlight,
    after,
    ariaHidden,
}: {
    before: string;
    highlight: string;
    after: string;
    ariaHidden?: boolean;
}) {
    const theme = useTheme();

    return (
        <Typography
            component="span"
            aria-hidden={ariaHidden}
            sx={segmentTypographySx}
        >
            {before}
            <Box
                component="span"
                sx={{
                    color: theme.palette.secondary.light,
                    fontWeight: 800,
                    textShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.45)}`,
                }}
            >
                {highlight}
            </Box>
            {after}
            {'  ✈️  '}
        </Typography>
    );
}

function MarqueeTrack({
    before,
    highlight,
    after,
    ariaHidden,
}: {
    before: string;
    highlight: string;
    after: string;
    ariaHidden?: boolean;
}) {
    return (
        <Box
            component="span"
            aria-hidden={ariaHidden}
            sx={{
                display: 'flex',
                flexShrink: 0,
                alignItems: 'center',
            }}
        >
            {Array.from({ length: REPEAT_COUNT }).map((_, index) => (
                <MarqueeSegment
                    key={index}
                    before={before}
                    highlight={highlight}
                    after={after}
                    ariaHidden={ariaHidden}
                />
            ))}
        </Box>
    );
}

export default function PromoMarquee() {
    const { t } = useTranslation();
    const theme = useTheme();
    const before = t('promoMarquee.before');
    const highlight = t('promoMarquee.highlight');
    const after = t('promoMarquee.after');
    const primary = theme.palette.primary.main;

    return (
        <Box
            sx={{
                overflow: 'hidden',
                width: '100%',
                minHeight: PROMO_MARQUEE_HEIGHT_PX,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                background: `linear-gradient(
                    90deg,
                    ${darken(primary, 0.22)} 0%,
                    ${primary} 35%,
                    ${lighten(primary, 0.12)} 65%,
                    ${darken(primary, 0.12)} 100%
                )`,
                color: '#fff',
                borderTop: `1px solid ${alpha('#fff', 0.15)}`,
                '@keyframes promoMarquee': {
                    '0%': { transform: 'translateX(0)' },
                    '100%': { transform: 'translateX(-50%)' },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    width: 'max-content',
                    animation: 'promoMarquee 36s linear infinite',
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                        width: '100%',
                        justifyContent: 'center',
                    },
                }}
            >
                <MarqueeTrack before={before} highlight={highlight} after={after} />
                <MarqueeTrack before={before} highlight={highlight} after={after} ariaHidden />
            </Box>
        </Box>
    );
}
